import React, { useState, useEffect, useCallback } from 'react';
import { getDietOrdersForAdmin, restoreOrder } from '../api/dietitianApi';
import { Pagination } from '../components/Pagination';
import type { DietOrderListItem, DietOrderStatus } from '../types/dietitian.types';
import { toast } from '../../cabinet/components/ui/Toast';

interface AdminOrdersPageProps {
  onSelectOrder: (id: number) => void;
}

type AdminTab = 'all' | 'todo' | 'done' | 'cancelled';

const STATUS_BADGE: Record<DietOrderStatus, { label: string; cls: string }> = {
  'wc-diet-pending':   { label: 'Nowe', cls: 'b-new' },
  'wc-diet-confirmed': { label: 'W przygotowaniu', cls: 'b-today' },
  'wc-diet-done':      { label: 'Gotowe', cls: 'b-done' },
  'wc-cancelled':      { label: 'Anulowane', cls: 'b-cancel' },
  'cancelled':         { label: 'Anulowane', cls: 'b-cancel' },
};

export const AdminOrdersPage: React.FC<AdminOrdersPageProps> = ({ onSelectOrder }) => {
  const [orders, setOrders]   = useState<DietOrderListItem[]>([]);
  const [stats, setStats]     = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState<AdminTab>('all');
  const [search, setSearch]   = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => { setCurrentPage(1); }, [tab, search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDietOrdersForAdmin(tab);
      setOrders(res.orders || []);
      setStats(res.stats || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const filtered = orders.filter(o => {
    const match = !search || o.customer_name.toLowerCase().includes(search.toLowerCase()) || o.customer_email.toLowerCase().includes(search.toLowerCase());
    if (!match) return false;

    // Filter logic based on fetched orders. 
    // getDietOrdersForAdmin(tab) handles the heavy lifting, but we also filter client-side just in case
    const isDone = o.status === 'wc-diet-done' || o.has_pdf || o.has_zalecenia;
    const isCancelled = o.status === 'wc-cancelled' || o.status === 'cancelled';
    
    if (tab === 'todo') return !isDone && !isCancelled;
    if (tab === 'done') return isDone && !isCancelled;
    if (tab === 'cancelled') return isCancelled;
    return true;
  });

  const paginatedOrders = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const counts = {
    all:  stats?.total_all ?? '-',
    todo: stats?.total_todo ?? '-',
    done: stats?.total_done ?? '-',
    cancelled: stats?.total_cancelled ?? '-',
  };

  return (
    <div>
      {/* Admin badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1>Wszystkie zamówienia</h1>
          <div className="muted">Widok administratora · możesz dodawać notatki i zarządzać statusami</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            className="search"
            type="text"
            placeholder="Szukaj pacjenta..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '0 14px', height: 38, borderRadius: 12, border: '1.5px solid var(--lav)', outline: 'none', fontFamily: 'inherit', fontSize: 14 }}
          />
          <button className="btn ghost sm" onClick={load} style={{ padding: '0 12px', height: 38, fontSize: 16 }}>↺</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {(['all', 'todo', 'done', 'cancelled'] as AdminTab[]).map(t => (
          <div key={t} className={`tab ${tab === t ? 'on' : ''}`} onClick={() => setTab(t)}>
            {t === 'all' ? `Wszystkie (${counts.all})` : t === 'todo' ? `Do zrobienia (${counts.todo})` : t === 'done' ? `Gotowe (${counts.done})` : `Anulowane (${counts.cancelled})`}
          </div>
        ))}
      </div>

      {/* Order list */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ height: 72, background: 'linear-gradient(90deg, #f0ebfc, #e8e0f8)', borderRadius: 16, animation: 'pulse 1.5s ease-in-out infinite' }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--v600)' }}>
          <span style={{ fontSize: 48 }}>📋</span>
          <p>Brak zamówień w tej kategorii</p>
        </div>
      ) : (
        <div>
          {paginatedOrders.map(order => (
            <AdminOrderRow key={order.id} order={order} onOpen={() => onSelectOrder(order.id)} reload={load} />
          ))}
          <div style={{ marginTop: 24 }}>
            <Pagination
              totalItems={filtered.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const AdminOrderRow: React.FC<{ order: DietOrderListItem; onOpen: () => void; reload: () => void }> = ({ order, onOpen, reload }) => {
  const [restoring, setRestoring] = useState(false);
  const initials = order.customer_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  let badgeLabel = 'Nowe';
  let badgeClass = 'b-new';
  
  const isDone = order.status === 'wc-diet-done' || order.has_pdf || order.has_zalecenia;
  const isCancelled = order.status === 'wc-cancelled' || order.status === 'cancelled';
  
  if (isCancelled) {
    badgeClass = 'b-new';
    badgeLabel = 'Anulowane';
  } else if (isDone) {
    badgeClass = 'b-done';
    badgeLabel = 'Gotowe · wysłane';
  } else if (order.slot_confirmed || order.slot) {
    badgeClass = 'b-today';
    const activeSlot = (order.slot_confirmed || order.slot || '');
    const parts = activeSlot.split(' ');
    if (parts.length >= 2) {
      const d = parts[0].split('-');
      const t = parts[1].slice(0, 5);
      const tzOffset = (new Date()).getTimezoneOffset() * 60000;
      const todayStr = (new Date(Date.now() - tzOffset)).toISOString().split('T')[0];
      badgeLabel = parts[0] === todayStr ? `Dziś ${t}` : `${d[2]}.${d[1]} ${t}`;
    } else {
      badgeLabel = activeSlot;
    }
  } else if (order.status === 'wc-diet-confirmed' || order.has_admin_note || order.type === 'pakiet') {
    badgeClass = 'b-prep';
    badgeLabel = 'W przygotowaniu';
  }

  const shortDate = order.ordered_at ? order.ordered_at.split(' ')[0].slice(0, 5) : '';
  const subText = `${shortDate} · ${order.type_label}`;

  const handleRestore = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Czy na pewno chcesz przywrócić to zamówienie do realizacji?')) return;
    setRestoring(true);
    try {
      await restoreOrder(order.id);
      toast('Zamówienie przywrócone.');
      reload();
    } catch (err: any) {
      toast('Błąd podczas przywracania: ' + (err.response?.data?.error || err.message));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="row" onClick={onOpen} style={{ cursor: 'pointer' }}>
      <div className="av">{initials || '?'}</div>

      <div className="who">
        <div className="nm">{order.customer_name}</div>
        <div className="sub">{subText}</div>
      </div>

      <span className={`badge ${badgeClass}`}>{badgeLabel}</span>

      <div className="spacer"></div>

      {isCancelled ? (
        <>
          <button className="btn sm ghost" onClick={handleRestore} disabled={restoring}>
            {restoring ? 'Przywracanie...' : 'Przywróć'}
          </button>
          <button className="btn sm" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
            Otwórz
          </button>
        </>
      ) : !isDone ? (
        <>
          <button className="btn sm ghost" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
            Dodaj notatkę
          </button>
          <button className="btn sm" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
            Otwórz
          </button>
        </>
      ) : (
        <button className="btn sm ghost" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
          Podgląd
        </button>
      )}
    </div>
  );
};
